import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '@/hooks/useConsent';
import '@/redesign/system/tokens.css';
import '@/redesign/system/primitives.css';
import './CookieConsent.css';

/**
 * Global cookie-consent banner — a small redesign island at the App root, so
 * it renders in the "Words with a place to live" system on every route
 * (including the four legacy-token admin islands, whose chrome it is not part
 * of). The `.rd` wrapper scopes the redesign tokens to this subtree.
 *
 * Placement follows blueprint §3: transient surfaces live top-center below the
 * header — the bottom of the viewport stays a reserved action zone (rating
 * dock, commit bars) that nothing may occlude. CSS `top` must clear the
 * logged-out Sign in / Sign up pills (2026-08-28 ruling); sitting at the
 * viewport edge covered them on a phone first visit (Vidi-kur). The
 * legacy-token bottom bar predated that law. The enter animation stays a
 * short fade/nudge so the card never sweeps across those pills.
 *
 * Persistence: the decision is localStorage['vidi-consent'] via useConsent
 * (verified durable across reloads); Settings → Preferences carries the
 * matching analytics switch so the choice is re-manageable without clearing
 * site data.
 */
export default function CookieConsent() {
  const { t } = useTranslation('common');
  const { hasDecided, acceptAll, acceptEssential } = useConsent();

  return (
    <AnimatePresence>
      {!hasDecided && (
        <div className="rd">
          <motion.div
            initial={{ y: -12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -12, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30, delay: 0.5 }}
            className="rd-cookie"
            role="dialog"
            aria-label={t('cookies.dialogLabel')}
          >
            <div className="rd-cookie-inner">
              <div className="rd-cookie-body">
                <span className="rd-cookie-icon" aria-hidden="true">
                  <Cookie size={18} />
                </span>
                <div className="rd-cookie-copy">
                  <p className="rd-cookie-text">
                    {t('cookies.description')}{' '}
                    <a href="/privacy" className="rd-cookie-link">
                      {t('cookies.privacyLink')}
                    </a>
                  </p>
                  <p className="rd-cookie-note">
                    <Shield size={12} className="rd-cookie-shield" aria-hidden="true" />
                    {t('cookies.essentialNote')}
                  </p>
                </div>
              </div>
              <div className="rd-cookie-actions">
                <button type="button" className="rd-cookie-quiet" onClick={acceptEssential}>
                  {t('cookies.essentialOnly')}
                </button>
                <button
                  type="button"
                  className="rd-button-primary rd-cookie-accept"
                  onClick={acceptAll}
                >
                  {t('cookies.acceptAll')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
